import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLSettingsTabId } from '../../../settings/tabs/types';
import { SMLUpgradeFeature, pickPlanForFeature } from '../../config/music24PlanCapabilities';
import { planNameMessages } from '../../config/music24PlanNames';
import { formatPlanPrice, useDisplayCurrency } from '../../config/useDisplayCurrency';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { featureMessages } from '../../components/SMLUpgradeModal/upgradeMessages';
import { useIsNativeEmbed } from '../../navigation/hooks/useIsNativeEmbed';
import { fullVideoMessages } from './fullVideoMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FullVideoPlanNoteProps {
  /** The asset type of the card: the motion artwork has its own title and body. */
  themeId: StudioThemeId;
  /** Only the owner of the workspace upgrades (D11): a member sees whom to ask. */
  isOwner: boolean;
}

/**
 * The result first, then the plan and the price (pricing spec 2026-10-08): "Make a video of the
 * whole song" or "Make your cover move", then "On Pro, $39 a month." Full-song videos and the
 * motion artwork need Pro. The owner sees "See the plans" and "Upgrade"; a member sees "Ask the
 * owner of the workspace to upgrade.". Inside a native app's web view (App Store 3.1.1) the owner
 * sees no price and no purchase link.
 */
const FullVideoPlanNoteComponent: React.FC<FullVideoPlanNoteProps> = ({ themeId, isOwner }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isNativeEmbed = useIsNativeEmbed();
  const openUpgradePrompt = useUpgradePrompt();
  const currency = useDisplayCurrency();
  const current = useMusic24Capabilities();
  const isMotion = themeId === 'motionArtwork';
  const feature = isMotion ? SMLUpgradeFeature.motionArtwork : SMLUpgradeFeature.fullVideo;
  // A plan gate, not a credit shortage: the plan prompt of the result, never the video-credit copy.
  const upgrade = useCallback(() => openUpgradePrompt(feature), [openUpgradePrompt, feature]);
  const plan = pickPlanForFeature(feature, current);
  const planName = intl.formatMessage(planNameMessages[plan.tier]);
  const onPlan = isNativeEmbed
    ? intl.formatMessage(featureMessages.resultLedeWithoutPrice, { plan: planName })
    : intl.formatMessage(featureMessages.resultLede, {
        plan: planName,
        price: formatPlanPrice(intl, plan.prices.monthly.amount, currency),
      });
  const seePlans = useCallback(() => router.push(SMLRoutes.settings, { tab: SMLSettingsTabId.subscription }), [router]);
  const showBuy = isOwner && !isNativeEmbed;
  const ownerBody = isMotion ? fullVideoMessages.motionPlanBodyOwner : fullVideoMessages.planBodyOwner;

  return (
    <div className={styles.planNote} data-testid="studio-plan-note">
      <b>{intl.formatMessage(isMotion ? fullVideoMessages.motionPlanTitle : fullVideoMessages.planTitle)}</b>
      {isOwner && <span data-testid="studio-plan-note-price">{onPlan}</span>}
      <span>{intl.formatMessage(isOwner ? ownerBody : fullVideoMessages.planBodyMember)}</span>
      {showBuy && (
        <span className={styles.planActions}>
          <button type="button" className="btn btn-link btn-sm p-0" onClick={seePlans}>
            {intl.formatMessage(fullVideoMessages.planSeePlans)}
          </button>
          <SMLButton size="sm" outline omitCap onClick={upgrade}>
            {intl.formatMessage(fullVideoMessages.planUpgrade)}
          </SMLButton>
        </span>
      )}
    </div>
  );
};

export const FullVideoPlanNote = TypedMemo(FullVideoPlanNoteComponent);
